import { ArticleGridCards } from "@components/Cards/Layout/ArticleGridCards";
import { ArticleStackCards } from "@components/Cards/Layout/ArticleStackCards";
import MainContainer from "@components/Container/MainContainer";
import { LOCALHOST } from "@config/MetaTagsConstants";
import { AdsLb2, AdsLb3 } from "@elements/Advertisement/variant/AdsLb";
import PromoteEpaper from "@elements/Banner/PromoteEpaper/PromoteEpaper";
import Heading from "@elements/Typography/Heading";
import { getGTMData } from "@helper/getMetaData";
import { useWindowSize } from "@hooks/useWindowSize/useWindowSize";
import { Route } from "@route-types/Keyword/+types/Keyword";
import ErrorPage from "@routes/StatusHandler/StatusHandler";
import {
  fetchContext,
  ResponseType,
  type TRouteWithoutRedirect,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { createMetaTags, type MetaTagsProps } from "@sphtech/web2-seo-schema";
import { type ProcessedArticleData } from "@transformer/useOsResponse";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import type { GADataProps } from "@typings/PageMetaData";
import { KeywordPayloadResponseType } from "@typings/PagePayloadType";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";

import fetchKeywordData from "./Keyword.server";
import KeywordInfiniteScroll from "./KeywordInfiniteScroll";

export function meta(args: Route.MetaArgs) {
  const dataLoaderResponse = args.loaderData as TRouteWithRedirect<
    KeywordPayloadResponseType,
    string
  >;
  if (dataLoaderResponse.type === ResponseType.SUCCESS) {
    const metaData = dataLoaderResponse.payload.metaTags as MetaTagsProps;
    return createMetaTags(metaData);
  }
}

export default function KeywordPage() {
  const dataLoaderResponse: TRouteWithoutRedirect<
    KeywordPayloadResponseType,
    string
  > = useLoaderData();
  const { isScreenMD } = useWindowSize();

  if (dataLoaderResponse.type) {
    return <ErrorPage response={dataLoaderResponse} />;
  }

  const keywordArticles = dataLoaderResponse.payload.data;

  const firstFiveArticles = keywordArticles.slice(0, 5);
  const remainingArticles = keywordArticles.slice(5);

  // Function to split articles into array of 8
  const loadedkeywordArticles = [
    ...(!isScreenMD ? remainingArticles : keywordArticles),
  ].reduce<ProcessedArticleData[][]>((sections, context, index) => {
    if (index % 8 === 0) sections.push([]);
    sections[sections.length - 1].push(context);
    return sections;
  }, []);

  const tagAlias = dataLoaderResponse.payload.tagAlias;

  let tagName = "";
  for (const tag of keywordArticles[0].tags || []) {
    if (tag.alias?.toLowerCase().includes(tagAlias.toLowerCase())) {
      if (tag.name !== undefined) {
        tagName = tag.name;
        break;
      }
    }
  }
  const epaperCoverImageUrl =
    dataLoaderResponse.payload.epaperCoverImage.coverImageUri;

  const pageAdTargetValue = PageAdTargetingTypeEnum.TAG;
  const sectionName = "keywords";

  return (
    <MainContainer
      pageAdTargetValue={pageAdTargetValue}
      sectionName={sectionName}
    >
      <div className="flex flex-col lg:flex-row justify-between">
        <div className="w-full mt-xxs lg:mt-x2l">
          <Heading className="max-w-7xl bg-white pb-2.5 text-grey-200 text-40 lg:text-100 font-bold text-center uppercase font-opensans w-max mx-auto border-t-[3px] border-solid border-grey-600">
            {tagName || tagAlias}
          </Heading>
          {!isScreenMD && (
            <div className="block md:hidden">
              <ArticleStackCards
                articles={firstFiveArticles}
                rootClassName="max-w-7xl bg-white m-auto px-3"
                hasShowAuthor={true}
              />
              <AdsLb2
                sectionName={sectionName}
                pageAdTargetType={pageAdTargetValue}
                rootClassName={
                  "mb-10 mt-5 relative w-full bg-grey-950 py-xxm pt-2.5 pb-5 md:px-2 md:py-xxs lg:pt-4 lg:pb-6 lg:px-0"
                }
                index={0}
              />
            </div>
          )}

          {loadedkeywordArticles.map((section, sectionIndex) => (
            <>
              <ArticleGridCards
                article={section}
                articleIndex={sectionIndex}
                rootClassName="px-3 xl:p-0 gap-3 md:inline-grid md:grid-cols-4 xmd:inline-grid lg:grid xmd:pb-10 lg:max-w-7xl bg-white m-auto"
                cardWrapper="gap-2 xmd:py-0 md:flex-col md:max-w-[210px] xmd:max-w-full"
                imageWrapperStyle="md:max-w-[171px] xmd:w-full"
                hasShowAuthor={true}
              />
              {sectionIndex == 0 && isScreenMD && (
                <AdsLb2
                  sectionName={sectionName}
                  pageAdTargetType={pageAdTargetValue}
                  rootClassName={
                    "mb-10 mt-5 xmd:mt-10 relative w-full bg-grey-950 py-xxm pt-2.5 pb-5 md:px-2 md:py-xxs lg:pt-4 lg:pb-6 lg:px-0"
                  }
                  index={sectionIndex}
                />
              )}
              {sectionIndex > 0 &&
                sectionIndex < loadedkeywordArticles.length - 1 && (
                  <AdsLb3
                    sectionName={sectionName}
                    pageAdTargetType={pageAdTargetValue}
                    rootClassName={
                      "mb-10 mt-5 xmd:mt-10 relative w-full bg-grey-950 py-xxm pt-2.5 pb-5 md:px-2 md:py-xxs lg:pt-4 lg:pb-6 lg:px-0"
                    }
                    index={isScreenMD ? sectionIndex : sectionIndex + 1}
                  />
                )}
            </>
          ))}

          <PromoteEpaper
            className="relative w-full xmd:mt-3"
            epaperCoverImageUrl={epaperCoverImageUrl}
          />

          <KeywordInfiniteScroll
            keywordArticles={keywordArticles}
            tagAlias={tagAlias}
            pageAdTargetValue={pageAdTargetValue}
          />
        </div>
      </div>
    </MainContainer>
  );
}

export async function loader({ params, request }: Route.LoaderArgs) {
  const keyword = params.level1;
  const data = await fetchKeywordData(keyword || "");

  const url = new URL(request.url, LOCALHOST);
  const gtmData = getGTMData({
    title: `${keyword}_Index`,
    level2: "keywords",
    url: url.toString(),
  } as GADataProps);
  return {
    ...data,
    gtm: gtmData,
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<KeywordPayloadResponseType, string>
  >;
}
